import React from 'react';
import styled from 'styled-components';
import Card from 'components/Card';
import AnimatedMetric from 'pages/track/Customers/components/Customers/AnimatedMetric';
import ComponentDemo from '../../components/ComponentDemo';
import CodeBlock from '../../components/CodeBlock';

const PageHeader = styled.div`
  margin-bottom: 48px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const SectionDescription = styled.p`
  font-size: 15px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  line-height: 1.7;
  margin: 0 0 32px 0;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin: 24px 0;
`;

const MetricsDocs = () => {
  const [metricValue, setMetricValue] = React.useState(1250);

  const handleRefresh = () => {
    setMetricValue(Math.floor(Math.random() * 2000) + 500);
  };

  return (
    <div>
      <PageHeader>
        <Title>Metrics Pattern</Title>
        <Subtitle>
          Animated metric cards for displaying key statistics and KPIs. Features smooth
          animations, multiple format types, and change indicators.
        </Subtitle>
      </PageHeader>

      <Section>
        <SectionTitle>Live Example</SectionTitle>
        <SectionDescription>
          Click "Refresh" to see the animation in action. The AnimatedMetric component
          automatically animates value changes.
        </SectionDescription>

        <ComponentDemo label="AnimatedMetric Component" align="stretch">
          <div style={{ width: '100%' }}>
            <button
              onClick={handleRefresh}
              style={{
                padding: '8px 16px',
                marginBottom: '16px',
                background: '#fa4616',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              Refresh Value
            </button>
            <MetricsGrid>
              <AnimatedMetric
                value={metricValue}
                label="Total Customers"
                changeText="+125 this week"
                isPositive={true}
                icon={
                  <svg fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                }
              />
            </MetricsGrid>
          </div>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Metric Variations</SectionTitle>
        <SectionDescription>
          The AnimatedMetric component supports various formatting options for different
          data types.
        </SectionDescription>

        <ComponentDemo label="Different Metric Types" align="stretch">
          <MetricsGrid>
            <AnimatedMetric
              value={450}
              label="Total Count"
              changeText="+24 this week"
              isPositive={true}
              icon={
                <svg fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              }
            />

            <AnimatedMetric
              value={12500}
              label="Revenue"
              changeText="+$1,250"
              isPositive={true}
              isCurrency={true}
              icon={
                <svg fill="currentColor" viewBox="0 0 16 16">
                  <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z" />
                </svg>
              }
            />

            <AnimatedMetric
              value={87.5}
              label="Success Rate"
              changeText="+2.5%"
              isPositive={true}
              isPercentage={true}
              decimals={1}
              icon={
                <svg fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
              }
            />

            <AnimatedMetric
              value={3.2}
              label="Avg Events/Customer"
              changeText="-0.3 events"
              isPositive={false}
              decimals={1}
              icon={
                <svg fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z" />
                </svg>
              }
            />
          </MetricsGrid>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Component Location</SectionTitle>
        <SectionDescription>
          The AnimatedMetric component is located at{' '}
          <code>
            /pages/track/Customers/components/Customers/AnimatedMetric.js
          </code>
        </SectionDescription>
      </Section>

      <Section>
        <SectionTitle>Basic Usage</SectionTitle>
        <CodeBlock>
{`import AnimatedMetric from 'pages/track/Customers/components/Customers/AnimatedMetric';

// Basic number metric
<AnimatedMetric
  value={1250}
  label="Total Customers"
  changeText="+125 this week"
  isPositive={true}
  icon={<svg>...</svg>}
/>

// Currency metric
<AnimatedMetric
  value={12500}
  label="Revenue"
  changeText="+$1,250"
  isPositive={true}
  isCurrency={true}
  icon={<svg>...</svg>}
/>

// Percentage metric
<AnimatedMetric
  value={87.5}
  label="Success Rate"
  changeText="+2.5%"
  isPositive={true}
  isPercentage={true}
  decimals={1}
  icon={<svg>...</svg>}
/>

// Decimal number metric
<AnimatedMetric
  value={3.2}
  label="Avg Events/Customer"
  changeText="-0.3 events"
  isPositive={false}
  decimals={1}
  icon={<svg>...</svg>}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Props</SectionTitle>
        <Card headerBorder title="AnimatedMetric Props">
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            • <strong>value</strong> (number): The numeric value to display
            <br />
            • <strong>label</strong> (string, required): Label text for the metric
            <br />
            • <strong>changeText</strong> (string): Text showing the change (e.g., "+125 this week")
            <br />
            • <strong>isPositive</strong> (boolean): Whether change is positive (green) or negative (red) - default: true
            <br />
            • <strong>isCurrency</strong> (boolean): Format value as currency - default: false
            <br />
            • <strong>isPercentage</strong> (boolean): Format value as percentage - default: false
            <br />
            • <strong>decimals</strong> (number): Number of decimal places - default: 0
            <br />
            • <strong>duration</strong> (number): Animation duration in ms - default: 1500
            <br />
            • <strong>icon</strong> (node): Icon element to display on the right
          </div>
        </Card>
      </Section>

      <Section>
        <SectionTitle>Grid Layout Pattern</SectionTitle>
        <SectionDescription>
          Use a responsive grid layout for displaying multiple metrics. This pattern
          automatically adjusts columns based on screen size.
        </SectionDescription>

        <CodeBlock>
{`import styled from 'styled-components';

const MetricsGrid = styled.div\`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin-bottom: 30px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    display: flex;
    overflow-x: auto;
    gap: 10px;
    -webkit-overflow-scrolling: touch;
  }
\`;

// Usage
<MetricsGrid>
  <AnimatedMetric ... />
  <AnimatedMetric ... />
  <AnimatedMetric ... />
  <AnimatedMetric ... />
  <AnimatedMetric ... />
</MetricsGrid>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Animation Hook</SectionTitle>
        <SectionDescription>
          The AnimatedMetric component uses the <code>useCountAnimation</code> hook
          internally to smoothly animate value changes.
        </SectionDescription>

        <CodeBlock>
{`// hooks/useCountAnimation.js
import { useEffect, useState } from 'react';

const useCountAnimation = (end, duration = 1500) => {
  const [count, setCount] = useState(end);

  useEffect(() => {
    let startTime;
    let animationFrame;
    const start = count;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(start + (end - start) * easeOutQuart);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [end, duration]);

  return count;
};

export default useCountAnimation;`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Real-World Example</SectionTitle>
        <SectionDescription>
          This is how metrics are used in the Customers page.
        </SectionDescription>

        <CodeBlock>
{`import AnimatedMetric from './AnimatedMetric';
import useCustomerAnalytics from './hooks/useCustomerAnalytics';

const Customers = ({ accountId }) => {
  const {
    totalCustomers,
    avgLifetimeValue,
    avgEventsPerCustomer,
    avgCustomersPerEvent,
    superFans,
    stats,
  } = useCustomerAnalytics(accountId);

  return (
    <MetricsGrid>
      <AnimatedMetric
        value={totalCustomers}
        label="Total Customers"
        changeText={stats?.totalCustomers?.changeText || '+0 this week'}
        isPositive={stats?.totalCustomers?.isPositive !== false}
        icon={<svg>...</svg>}
      />

      <AnimatedMetric
        value={avgLifetimeValue}
        label="Avg Lifetime Value"
        changeText={stats?.avgLifetimeValue?.changeText || '+$0'}
        isPositive={stats?.avgLifetimeValue?.isPositive !== false}
        isCurrency={true}
        icon={<svg>...</svg>}
      />

      <AnimatedMetric
        value={avgEventsPerCustomer}
        label="Avg Events/Customer"
        changeText={stats?.avgEventsPerCustomer?.changeText || '+0 events'}
        isPositive={stats?.avgEventsPerCustomer?.isPositive !== false}
        decimals={1}
        icon={<svg>...</svg>}
      />

      <AnimatedMetric
        value={avgCustomersPerEvent}
        label="Avg Customers/Event"
        changeText={stats?.avgCustomersPerEvent?.changeText || '+0'}
        isPositive={stats?.avgCustomersPerEvent?.isPositive !== false}
        decimals={0}
        icon={<svg>...</svg>}
      />

      <AnimatedMetric
        value={superFans}
        label="Super Fan"
        changeText={stats?.superFans?.changeText || '+0 new'}
        isPositive={stats?.superFans?.isPositive !== false}
        icon={<svg>...</svg>}
      />
    </MetricsGrid>
  );
};`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Best Practices</SectionTitle>
        <ComponentDemo direction="column" gap="16px">
          <Card title="✅ Do" accentTitle>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              • Use appropriate format types (currency, percentage, decimal)
              <br />
              • Show meaningful change indicators with context
              <br />
              • Use icons that represent the metric type
              <br />
              • Set appropriate decimal places for readability
              <br />
              • Use consistent animation duration across metrics
              <br />• Group related metrics together in grids
            </div>
          </Card>

          <Card title="❌ Don't" accentTitle>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              • Don't use very long animation durations (keep under 2 seconds)
              <br />
              • Don't show too many decimal places for large numbers
              <br />
              • Don't use misleading positive/negative indicators
              <br />
              • Don't overload users with too many metrics at once
              <br />• Don't forget to handle loading and error states
            </div>
          </Card>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Common Use Cases</SectionTitle>
        <Card>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            <strong>Customer Analytics:</strong>
            <br />• Total customers, active users, new signups
            <br />• Customer lifetime value, average order value
            <br />• Retention rate, churn rate
            <br />
            <br />
            <strong>Sales Metrics:</strong>
            <br />• Revenue, orders, conversion rate
            <br />• Average order value, items per order
            <br />• Sales growth, revenue per customer
            <br />
            <br />
            <strong>Event Metrics:</strong>
            <br />• Total events, attendees per event
            <br />• Ticket sales, revenue per event
            <br />• Event capacity utilization
            <br />
            <br />
            <strong>Engagement Metrics:</strong>
            <br />• Page views, session duration
            <br />• Bounce rate, click-through rate
            <br />• User engagement score
          </div>
        </Card>
      </Section>
    </div>
  );
};

export default MetricsDocs;